/**
 * Single source of truth für die Sidebar-Navigation.
 *
 * Jeder Eintrag deklariert die nötigen Permissions; die Sidebar rendert nur
 * was der Operator auch sehen darf. Permission-Strings sind
 * `me.permissions[]` aus `/v1/operator/me` (siehe operator-bootstrap.md).
 *
 * Routes hier MÜSSEN mit `routes.jsx` deckungsgleich sein.
 */

/**
 * @typedef {Object} NavItem
 * @property {string} label
 * @property {string} icon
 * @property {string} [to]          Sub-Items unterstützen via `children`
 * @property {string[]} [perms]
 * @property {string[]} [anyOf]
 * @property {NavItem[]} [children]
 */

/** @param {string} brandSlug @returns {NavItem[]} */
export function buildContextualNav(brandSlug) {
  return [
    {
      icon: 'pe-7s-graph2',
      label: 'Dashboard',
      to: `/${brandSlug}/dashboard`,
      perms: ['dashboard.read'],
    },
    {
      icon: 'pe-7s-mail',
      label: 'Tickets',
      to: `/${brandSlug}/tickets`,
      perms: ['tickets.read'],
    },
    {
      icon: 'pe-7s-users',
      label: 'Benutzer',
      to: `/${brandSlug}/users`,
      perms: ['users.read'],
    },
    {
      icon: 'pe-7s-cart',
      label: 'Orders & Billing',
      anyOf: ['orders.read', 'subscriptions.read', 'cancelIntents.read'],
      children: [
        { label: 'Orders', to: `/${brandSlug}/orders`, perms: ['orders.read'] },
        { label: 'Subscriptions', to: `/${brandSlug}/subscriptions`, perms: ['subscriptions.read'] },
        { label: 'Cancel-Intents (§ 312q)', to: `/${brandSlug}/cancel-intents`, perms: ['cancelIntents.read'] },
      ],
    },
    {
      icon: 'pe-7s-box1',
      label: 'Catalog',
      anyOf: ['products.read', 'usage.read'],
      children: [
        { label: 'Products', to: `/${brandSlug}/products`, perms: ['products.read'] },
        { label: 'Usage', to: `/${brandSlug}/usage`, perms: ['usage.read'] },
      ],
    },
    {
      icon: 'pe-7s-key',
      label: 'API-Keys',
      to: `/${brandSlug}/api-keys`,
      perms: ['apiKeys.read'],
    },
    {
      icon: 'pe-7s-plug',
      label: 'Integrations',
      anyOf: ['drivers.read', 'integrations.read', 'stripeConnect.read', 'outboundWebhooks.read'],
      children: [
        { label: 'Driver-Status', to: `/${brandSlug}/drivers`, perms: ['drivers.read'] },
        { label: 'Provider-Credentials', to: `/${brandSlug}/integrations`, perms: ['integrations.read'] },
        { label: 'Stripe-Connect', to: `/${brandSlug}/stripe-connect`, perms: ['stripeConnect.read'] },
        { label: 'Outbound-Webhooks', to: `/${brandSlug}/outbound-webhooks`, perms: ['outboundWebhooks.read'] },
      ],
    },
    {
      icon: 'pe-7s-news-paper',
      label: 'Compliance',
      anyOf: ['audit.read', 'oss.read', 'policy.read'],
      children: [
        { label: 'Audit-Log', to: `/${brandSlug}/audit`, perms: ['audit.read'] },
        { label: 'OSS-Report (§ 18 UStG)', to: `/${brandSlug}/oss`, perms: ['oss.read'] },
        { label: 'AGB-Policy (§ 312j)', to: `/${brandSlug}/policy`, perms: ['policy.read'] },
      ],
    },
    {
      // Brand-Settings ist single-page (PUT /v1/admin/tenant-settings).
      // Packages/Pricing/SLA-Sub-Items wurden entfernt — Core hat dafür
      // keine eigenen Endpoints (siehe orbit-specs/done/admin-brand-settings-endpoints).
      icon: 'pe-7s-settings',
      label: 'Brand-Settings',
      to: `/${brandSlug}/brand-settings`,
      perms: ['tenantSettings.read'],
    },
  ];
}

/** @type {NavItem[]} */
export const ADMIN_NAV = [
  {
    icon: 'pe-7s-graph2',
    label: 'Übersicht',
    to: '/admin',
    perms: ['appSettings.read'],
  },
  {
    icon: 'pe-7s-server',
    label: 'Infrastructure',
    to: '/admin/infra',
    perms: ['appSettings.read'],
  },
  {
    icon: 'pe-7s-tools',
    label: 'Diagnostics',
    to: '/admin/diagnostics',
    perms: ['appSettings.read'],
  },
  {
    // Core-API Settings — Apps-Management entfernt (Phantom), Stripe/Lex
    // sind unter /admin/integrations, SLA ist Phantom.
    icon: 'pe-7s-portfolio',
    label: 'Core API',
    perms: ['appSettings.read'],
    children: [
      { label: 'Übersicht', to: '/admin/settings/core' },
      { label: 'App-Settings', to: '/admin/settings/app' },
      { label: 'Tax-Rates', to: '/admin/settings/core/tax-rates' },
      { label: 'Integrations', to: '/admin/integrations', perms: ['integrations.read'] },
    ],
  },
  {
    // Nexus-API — Plugin-/Provisioning-Layer. Per orbit-specs INBOX 8 Specs;
    // Plugin-Registry ist Spec 1, andere folgen iterativ.
    icon: 'pe-7s-config',
    label: 'Nexus API',
    perms: ['integrations.read'],
    children: [
      { label: 'Übersicht', to: '/admin/settings/nexus' },
      { label: 'Plugin-Registry', to: '/admin/settings/nexus/plugins' },
      { label: 'Tenant-Routing', to: '/admin/settings/nexus/routing' },
      { label: 'System-Settings', to: '/admin/settings/nexus/settings' },
      { label: 'Jobs-Monitor', to: '/admin/settings/nexus/jobs' },
      { label: 'Saga-Review', to: '/admin/settings/nexus/saga' },
      { label: 'Mail-Security', to: '/admin/settings/nexus/mail-security' },
      { label: 'Postmaster SOC', to: '/admin/settings/nexus/postmaster' },
      { label: 'Usage Explorer', to: '/admin/settings/nexus/usage' },
      { label: 'Customers', to: '/admin/settings/nexus/customers' },
      { label: 'Hosting-Operations', to: '/admin/settings/nexus/hosting-ops' },
    ],
  },
  {
    icon: 'pe-7s-network',
    label: 'Probe API',
    perms: ['integrations.read'],
    children: [
      { label: 'Übersicht', to: '/admin/settings/probe' },
      { label: 'Scraping', to: '/admin/settings/probe/scrape' },
      { label: 'WireGuard', to: '/admin/settings/probe/wireguard' },
      { label: 'KEDA', to: '/admin/settings/probe/keda' },
    ],
  },
  {
    icon: 'pe-7s-graph1',
    label: 'Pulse API',
    perms: ['integrations.read'],
    children: [
      { label: 'Übersicht', to: '/admin/settings/pulse' },
      { label: 'Zabbix', to: '/admin/settings/pulse/zabbix' },
      { label: 'Telemetry', to: '/admin/settings/pulse/telemetry' },
      { label: 'cpGuard', to: '/admin/settings/pulse/cpguard' },
    ],
  },
];
